import { ChartData, NameValuePair } from "../Shared/ProfileData/ChartData";
import React, { useEffect } from "react";


export type ChartDisplayProps = {
    chartData: ChartData,
    x: number,
    y: number,
    width: number,
    height: number,
    options: ChartDisplayOptions

}

export type YAxisCalculator = 'zero' | 'abs' | 'proportion' | 'variation';

export type ChartDisplayOptions = {

    showAxis: boolean;
    barWidth : number;
    barCSS: string[];
    barHighlightCSS: string[];
    axisColor: string;
    bottomLabelSpace: number;
    yCalculator : YAxisCalculator;
    yCalcParameter : number;
    valuePostfix: string;
    textHeight: number;
    xmin: number;
    xmax: number;
    isDate: boolean;
    idPrefix: string;

}

export const ChartDisplay = ({ chartData, x, y, width, height, options }: ChartDisplayProps): React.JSX.Element => {
    if (chartData.chartType === "bar")
        return <BarChart chartData={chartData} x={x} y={y} width={width} height={height} options={options} />
    else if (chartData.chartType === "donut")
        return <DonutChart chartData={chartData} x={x} y={y} width={width} height={height} options={options} />
    else if (chartData.chartType === "singleBar")
        return <SingleBarChart chartData={chartData} x={x} y={y} width={width} height={height} options={options} />
    else
        return <LineGraph chartData={chartData} x={x} y={y} width={width} height={height} options={options} />

}

const optionPicker = (options : string[], n : number, index : number) : string => {
    if(options.length == 1)
        return options[0];
    else if(options.length >= n)
        return options[index];
    else 
    {
        const oIndex = Math.round(options.length / n * index - 0.5);
        return options[oIndex];
        
    }
}


export const BarChart = ({ chartData, x, y, width, height, options }: ChartDisplayProps): React.JSX.Element => {

    const barSlot = width / chartData.data.length;
    const barWidth = barSlot * options.barWidth;
    const barOffset = barSlot * (1-options.barWidth) * 0.5;
    const yAxis = height - options.bottomLabelSpace - 20;
    const yHeight = yAxis - 120;

    //var dataMin = Number.MAX_VALUE;
    var dataMax = Number.MIN_VALUE;
    chartData.data.forEach((nvp: NameValuePair) => {
        //    dataMin = Math.min(dataMin, nvp.value);
        dataMax = Math.max(dataMax, nvp.value);
    });

    var minValue = 0;

    if(options.yCalculator === 'abs')
        minValue = options.yCalcParameter;
    else if(options.yCalculator === 'proportion')
    {
        var dataMin = Number.MAX_VALUE;
        chartData.data.forEach((nvp: NameValuePair) => {
            //    dataMin = Math.min(dataMin, nvp.value);
            dataMin = Math.min(dataMin, nvp.value);
        });
        minValue = options.yCalcParameter * dataMin;
    }
    else if(options.yCalculator === 'variation')
    {
        var dataMin = Number.MAX_VALUE;
        chartData.data.forEach((nvp: NameValuePair) => {
            //    dataMin = Math.min(dataMin, nvp.value);
            dataMin = Math.min(dataMin, nvp.value);
        });
        minValue = (1 - options.yCalcParameter) * dataMax + options.yCalcParameter * dataMin;
    }


    const yFactor = yHeight / (dataMax - minValue);




    return (<g transform={"translate(" + x + "," + y + ")"}>
        <text x="30" y="50" className="headingText">{chartData.title}</text>
        {
            options.showAxis && <line x1="0" x2="0" y1="25" y2={yAxis} stroke={options.axisColor} />
        }
        {
            options.showAxis && <line x1="0" x2={width} y1={yAxis} y2={yAxis} stroke={options.axisColor} />
        }
        {
            chartData.data.map((nvp: NameValuePair, i: number) => {
                const xp = barOffset + i * barSlot;
                const height = (nvp.value - minValue) * yFactor;
                //const css = i >= options.barCSS.length ? options.barCSS[options.barCSS.length - 1] : options.barCSS[i];
                return (
                    <rect key={'BarChartBar' + i} x={xp} width={barWidth} height={height} y={yAxis - height} className={optionPicker(options.barCSS, chartData.data.length, i)} />
                )
            })


        }
        {
            chartData.data.map((nvp: NameValuePair, i: number) => {
                const xp = barOffset + (i + 0.25) * barSlot + 6;

                //return (<text x="0" y="0" transform={"rotate(90) translate(" + (yAxis + 10) + ",-" + (xp + 2) + ")"} className="barLabel">{nvp.name}</text>);
                return (<g key={'BarChartLabel' + i} transform={"rotate(90) translate(" + (yAxis + 10) + ",-" + (xp + 2) + ")"}><foreignObject x="0" y="0" width={options.bottomLabelSpace} height={barSlot}>
                    <div style={{width : options.bottomLabelSpace + "px", display : "flex"}}><span className="barLabel">{nvp.name}</span></div>
                    </foreignObject></g>)
            })
        }

        {
            chartData.data.map((nvp: NameValuePair, i: number) => {
                const xp = (i + 0.5) * barSlot;
                const barHeight = (nvp.value - minValue) * yFactor;
                return (<text key={'BarChartValueLabel' + i} x={xp} y={yAxis - barHeight - 2} className="barLabel" textAnchor="middle">{nvp.value + options.valuePostfix}</text>); //x={nvp.value < 100 ? "" + xp : "" + (xp - 4)}
            })
        }
    </g>);

}

export const SingleBarChart = ({ chartData, x, y, height, width, options }: ChartDisplayProps): React.JSX.Element => {

    var totalValue = 0;

    for (let i = 0; i < chartData.data.length; i++) {
        totalValue += chartData.data[i].value;
    }
    const sf = width / totalValue;
    var xv = new Array<number>();
    var widths = new Array<number>();
    const labelY = 0.5 * (height - options.textHeight) + 12.5;


    totalValue = 0;

    for (let i = 0; i < chartData.data.length; i++) {
        xv.push(totalValue);
        widths.push(sf * chartData.data[i].value);
        totalValue += sf * chartData.data[i].value;
    }

    return (<g transform={"translate(" + x + "," + y + ")"}>
        <text x="0" y="20" className="headingText">{chartData.title}</text>
        {
            chartData.data.map((value: NameValuePair, index: number) => {
                //const css = index >= options.barCSS.length ? options.barCSS[options.barCSS.length - 1] : options.barCSS[index];
                return (<rect key={'SingleBarSegment' + index} x={xv[index]} width={value.value * sf} y="25" height={height - 25} className={optionPicker(options.barCSS, chartData.data.length, index)} />)
            })
        }

        {
            chartData.data.map((value: { name: string, value: number }, index: number) => {
                return (<text key={'SingleBarSegmentLabel' + index} x={(xv[index] + 0.5 * widths[index])} y={labelY} className="barLabel" textAnchor="middle">{value.name}</text>)
            })
        }



    </g>);

}

export const DonutChart = ({ chartData, x, y, width, height, options }: ChartDisplayProps): React.JSX.Element => {

    const radius = Math.min(width, height) * 0.4;
    const innerRadius = 0.5 * radius;
    const txtRadius = radius + 10;
    var total = 0;
    for (let i = 0; i < chartData.data.length; i++) {
        total += chartData.data[i].value;
    }

    var factor = 2 * Math.PI / total;

    var theta = -Math.PI * 0.5;
    const cx = 0.5 * width + x;//(width - radius * 2) * 0.5 + radius;
    const cy = 0.5 * height + y; //(height - radius * 2) * 0.5 + radius;

    const pathStrings = new Array<string>();
    const textX = new Array<number>();
    const textY = new Array<number>();
    // const textWidths = new Array<number>();

    for (let i = 0; i < chartData.data.length; i++) {
        const span = chartData.data[i].value * factor;
        const endTheta = theta + span
        const txtTheta = theta + 0.5 * span;

        const p1 = [Math.round(cx + Math.cos(endTheta) * radius), Math.round(cy + Math.sin(endTheta) * radius)];
        const p2 = [Math.round(cx + Math.cos(endTheta) * innerRadius), Math.round(cy + Math.sin(endTheta) * innerRadius)];
        const p3 = [Math.round(cx + Math.cos(theta) * innerRadius), Math.round(cy + Math.sin(theta) * innerRadius)];
        const p4 = [Math.round(cx + Math.cos(theta) * radius), Math.round(cy + Math.sin(theta) * radius)];
        const pt = [Math.round(cx + Math.cos(txtTheta) * txtRadius), Math.round(cy + Math.sin(txtTheta) * txtRadius)];
        const arcflag = span > Math.PI ? 1 : 0;

        const m = "M " + p4[0] + " " + p4[1];
        const a1 = "A " + radius + " " + radius + " 0 " + arcflag + " 1 " + p1[0] + " " + p1[1];

        const l1 = "L " + p2[0] + " " + p2[1];

        const a2 = "A " + innerRadius + " " + innerRadius + " 0 " + arcflag + " 0 " + p3[0] + " " + p3[1];

        const l2 = "L " + p4[0] + " " + p4[1];

        pathStrings.push(m + " " + a1 + " " + l1 + " " + a2 + " " + l2);
        textX.push(pt[0]);
        textY.push(pt[1] + (0.5 * options.textHeight));

        theta = endTheta;

    }

    // x={measureContext === null ? 0 : (cx - 0.5 * measureContext.measureText(chartData.title ?? "").width)}
    // x={((theta < (-Math.PI * 0.5)) || (theta > (0.5 * Math.PI))) ? (textX[index] - textWidths[index]) : textX[index]}
    // <text x={cx} y={cy + 5} className="headingText">{chartData.title}</text>

    return (<g transform={"translate(" + x + "," + y + ")"}>
        <foreignObject x={cx - innerRadius + 10} y={cy- innerRadius + 10
        } width={innerRadius * 2 - 20} height={innerRadius * 2}>
                    <div style={{width : (innerRadius * 2 - 20) + "px", display : "flex"}}><span className="chartHeadingText">{chartData.title}</span></div>
                    </foreignObject>
       
        {
            pathStrings.map((value: string, index: number, _array: string[]) => {
                const css = options.barCSS[index % options.barCSS.length];
                return (<path key={'DonutChartPathString' + index} d={value} id={"DP_" + chartData.data[index].name} className={css} pointerEvents="none" />);
            })
        }
        {
            chartData.data.map((value: NameValuePair, index: number) => {

                const lTheta = Math.atan2(textX[index] - cx, textY[index]-cy);
                const anchor = lTheta < 0 ? 'end' : 'start'; //((lTheta < (-Math.PI * 0.5)) || (lTheta > (0.5 * Math.PI))) ? "start" : "end";
                //console.log('Donut Segment' + value.name + ' x=' + textX[index] + ' y=' + textY[index] + ' theta=' + lTheta + ' Anchor ' + anchor);

                return (<text key={'DonutChartLabel' + index} x={textX[index]} y={textY[index]} className="chartLabelText" textAnchor={anchor}>{value.name}</text>)
            })
        }
    </g>);

}

export const LineGraph = ({ chartData, x, y, width, height, options }: ChartDisplayProps): React.JSX.Element => {

    const isBarSelected = React.useRef<boolean>(false);
    const insideChart = React.useRef<boolean>(false);
    const selectedBar = React.useRef<null | SVGRectElement>(null);
    const selectedMarker = React.useRef<null | SVGCircleElement>(null);
    const containerRef = React.useRef<null | SVGGElement>(null);
    const markers = React.useRef<SVGCircleElement[]>([]);
    const bars = React.useRef<SVGRectElement[]>([]);

    const displayRect = React.useRef<null | SVGRectElement>(null);
    const displayX = React.useRef<null | SVGTextElement>(null);
    const displayY = React.useRef<null | SVGTextElement>(null)
    const displayGroup = React.useRef<null | SVGGElement>(null);
    const selectedItem = React.useRef<number>(-1);

    useEffect(() => {
        if (containerRef.current !== null) {
            const rect = containerRef.current.getBoundingClientRect();

            const markerArr = new Array<SVGCircleElement>();
            const barArr = new Array<SVGRectElement>();

            for (let i = 0; i < chartData.data.length - 1; i++) {
                const element = document.getElementById(options.idPrefix + "Marker" + i);
                if (element !== null) {
                    markerArr.push((element as unknown) as SVGCircleElement);
                }

                const bar = document.getElementById(options.idPrefix + "Bar" + i);
                if(bar !== null)
                    barArr.push((bar as unknown) as SVGRectElement);
            }
            {
                const element = document.getElementById(options.idPrefix + "DisplayGroup");
                if (element !== null) {
                    displayGroup.current = ((element as unknown) as SVGGElement);
                }
            }
            {
                const element = document.getElementById(options.idPrefix + "DisplayRect");
                if (element !== null) {
                    displayRect.current = ((element as unknown) as SVGRectElement);
                }
            }
            {
                const element = document.getElementById(options.idPrefix + "DisplayX");
                if (element !== null) {
                    displayX.current = ((element as unknown) as SVGTextElement);
                }
            }
            {
                const element = document.getElementById(options.idPrefix + "DisplayY");
                if (element !== null) {
                    displayY.current = ((element as unknown) as SVGTextElement);
                }
            }



            markers.current = markerArr;
            bars.current = barArr;
        }
    })




    //displatGroup.setAttribute("transform", "translate(" + xpos + "," + ypos + ")");

    const barWidth = width / chartData.data.length;
    const yBase = 95; // 70 for overlay display and 25 for title
    var maxData = 0;
    for (let i = 0; i < chartData.data.length; i++)
        maxData = Math.max(maxData, chartData.data[i].value);

    const yScale = (height - yBase) / maxData;


    return (
        <g transform={"translate(" + x + "," + y + ")"} ref={containerRef}>
            <text x={width * 0.5} y="50" className="headingText" textAnchor="middle">{chartData.title}</text>
            {
                chartData.data.map((nvp: NameValuePair, i: number) => {
                    if (i < (chartData.data.length - 1)) {
                        const x0 = i * barWidth;
                        const x1 = (i + 1) * barWidth;
                        const y1 = Math.round(height - yScale * chartData.data[i].value);
                        const y2 = Math.round(height - yScale * chartData.data[i + 1].value);
                        //const css = i >= options.barCSS.length ? options.barCSS[options.barCSS.length - 1] : options.barCSS[i];

                        const pathString = "M " + x0 + " " + height + " V " + y1 + " L " + x1 + " " + y2 + " V " + height + " H " + x0 + " Z";
                        return <path key={'LineChartBar' + i} d={pathString} className={options.barCSS[0]} id={options.idPrefix + "Bar" + i}/>;
                    }
                    else
                        return false;
                })
            }
            {
                chartData.data.map((nvp: NameValuePair, i: number) => {
                    if (i < (chartData.data.length - 1)) {
                        const x0 = i * barWidth;
                        const y1 = Math.round(height - yScale * chartData.data[i].value);
                        const y2 = Math.round(height - yScale * chartData.data[i + 1].value);

                        const cx = x0 + 0.5 * barWidth;
                        const cy = (0.5 * (y1 + y2));
                        return (<circle key={'LineChartCircleMarker' + i} cx={cx} cy={cy} r={barWidth} className="LineGraphInvisibleMarker" id={options.idPrefix + "Marker" + i} />);
                    }
                    else
                        return false;
                })
            }
            <g id={options.idPrefix + "DisplayGroup"}>
                <rect x="0" y="0" height="60" width="150" className="LineGraphInvisibleOverlay" id={options.idPrefix + "DisplayRect"} />
                <text x="10" y="30" className="LineGraphOverlayText" id={options.idPrefix + "DisplayX"}></text>
                <text x="10" y="50" className="LineGraphOverlayText" id={options.idPrefix + "DisplayY"}></text>
            </g>
            <rect x="0" y="25" width={width} height={height - 25} className="barchartMouseHandler"
                onMouseEnter={(e: React.MouseEvent<SVGRectElement, MouseEvent>) => {
                    console.log("enter(" + e.clientX + "," + e.clientY + ")");
                    insideChart.current = true;
                }}
                onMouseLeave={(e: React.MouseEvent<SVGRectElement, MouseEvent>) => {
                    console.log("leave(" + e.clientX + "," + e.clientY + ")");
                    insideChart.current = false;
                }}

                onMouseMove={(e: React.MouseEvent<SVGRectElement, MouseEvent>) => {
                    if (insideChart && containerRef.current !== null) {
                        const rect = containerRef.current.getBoundingClientRect();
                        const offsetX = e.clientX - rect.left;//boundingRect.current[0];
                        const offsetY = e.clientY - rect.top; //boundingRect.current[1];
                        var item = Math.round(offsetX / barWidth);
                        item = item > (chartData.data.length - 2) ? (chartData.data.length - 2) : item;
                        var y = Math.round(height - yScale * chartData.data[item + 1].value);

                        if (offsetY >= y) {
                            console.log("hit(" + offsetX + "," + offsetY + ")");
                            if (isBarSelected.current) {
                                if (selectedBar.current !== null) {
                                    //const css = selectedItem.current < 0 ? options.barCSS[0] : (selectedItem.current >= options.barCSS.length ? options.barCSS[options.barCSS.length - 1] : options.barCSS[selectedItem.current]);
                                    selectedBar.current.setAttribute("class", options.barCSS[0]);
                                }
                                if (selectedMarker.current !== null)
                                    selectedMarker.current.setAttribute("class", "LineGraphInvisibleMarker");
                            }
                            else
                                isBarSelected.current = true;
                            selectedBar.current = bars.current[item]; //e.target as SVGRectElement;
                            selectedMarker.current = markers.current[item];
                            selectedItem.current = item;
                            if (selectedBar.current !== null) {
                                //const css = selectedItem.current < 0 ? options.barHighlightCSS[0] : (selectedItem.current >= options.barHighlightCSS.length ? options.barHighlightCSS[options.barHighlightCSS.length - 1] : options.barHighlightCSS[selectedItem.current]);
                                selectedBar.current.setAttribute("class", options.barHighlightCSS[0]);
                            }
                            if (selectedMarker.current !== null)
                                selectedMarker.current.setAttribute("class", "LineGraphVisibleMarker");

                            if (displayX.current !== null) {
                                if (options.isDate) {
                                    const date = new Date();
                                    date.setTime(Math.round(options.xmin + (options.xmax - options.xmin) * (item / chartData.data.length)));
                                    const time = (date.getHours() < 10 ? "0" : "") + date.getHours() + ":" + (date.getMinutes() < 10 ? "0" : "") + date.getMinutes();
                                    displayX.current.textContent = time;
                                }
                                else
                                    displayX.current.textContent = '' + item;
                            }
                            if (displayY.current !== null)
                                displayY.current.textContent = chartData.data[item].value + options.valuePostfix;

                            if (displayRect.current !== null)
                                displayRect.current.setAttribute("class", "LineGraphVisibleOverlay");

                            if (displayGroup.current !== null) {
                                var xpos = item * barWidth - 75;
                                xpos = xpos < 2 ? 2 : (xpos > 448 ? 448 : xpos);
                                const ypos = y - 70;
                                displayGroup.current.setAttribute("transform", "translate(" + xpos + "," + ypos + ")");
                            }


                        }
                        else if (isBarSelected.current && (selectedBar.current !== null)) {
                            const css = selectedItem.current < 0 ? options.barCSS[0] : (selectedItem.current >= options.barCSS.length ? options.barCSS[options.barCSS.length - 1] : options.barCSS[selectedItem.current]);
                            selectedBar.current.setAttribute("class", css);
                            isBarSelected.current = false;
                            selectedBar.current = null;
                            if (selectedMarker.current !== null)
                                selectedMarker.current.setAttribute("class", "LineGraphInvisibleMarker");
                            selectedMarker.current = null;
                            if (displayRect.current !== null)
                                displayRect.current.setAttribute("class", "LineGraphInvisibleOverlay");
                            if (displayX.current !== null)
                                displayX.current.textContent = "";
                            if (displayY.current !== null)
                                displayY.current.textContent = "";
                        }

                    }
                }}

            />

        </g>
    );

}